import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { openContactForm } from "@/lib/contactForm";
import { guideCategoryLabel } from "@/lib/guideHub";
import { loadPreviewGuides } from "@/pages/designPreview/previewData";
import { PreviewCta } from "@/pages/designPreview/PreviewBits";
import { Reveal } from "@/pages/designPreview/previewMotion";
import { CategoryCluster, SceneIndex } from "@/pages/designPreview/PreviewVisuals";
import { categoryHue } from "@/pages/designPreview/previewTheme";

export default function GuidesPreview() {
  const [data, setData] = useState({ items: [], hub: null });
  const [hovered, setHovered] = useState(null);
  const [params] = useSearchParams();
  const category = params.get("category") || "";
  const base = "/design-preview/guides";

  useEffect(() => {
    loadPreviewGuides(category).then(setData).catch(() => setData({ items: [], hub: null }));
  }, [category]);

  const hub = data.hub || {};
  const categories = hub.categories || [];
  const items = data.items || [];
  const featured = (!category && items.find((row) => row.slug === "homepage-agency-criteria")) || items[0] || null;
  const rest = featured ? items.filter((row) => row.id !== featured.id) : items;
  const href = (id) => (id ? `${base}?category=${encodeURIComponent(id)}` : base);

  const counts = useMemo(() => {
    const map = {};
    items.forEach((row) => {
      map[row.category] = (map[row.category] || 0) + 1;
    });
    return map;
  }, [items]);

  const visibleRest = hovered ? rest.filter((row) => row.category === hovered) : rest;

  return (
    <div>
      {featured ? (
        <section className="dp-scene">
          <div className="dp-gridbg" />
          <SceneIndex n={1} />
          <div className="dp-guide-hero">
            <Reveal>
              <p className="dp-kicker">Featured Guide</p>
              <h1 className="dp-display">
                <Link to={featured.path}>{featured.title}</Link>
              </h1>
              <p className="dp-lead">{featured.summary || featured.answer}</p>
              <Link className="dp-cta" to={featured.path}>가이드 읽기</Link>
            </Reveal>
            <div className="dp-knowledge-map">
              <p className="dp-kicker">Knowledge Map</p>
              <CategoryCluster categories={categories} counts={counts} hovered={hovered} onHover={setHovered} hueOf={categoryHue} />
              <p className="dp-knowledge-map__hint">주제 노드에 마우스를 올리면 아래 목록이 필터링됩니다.</p>
            </div>
          </div>
        </section>
      ) : null}

      <nav className="dp-nav-text" aria-label="가이드 주제">
        <Link className={!category ? "is-on" : ""} to={base}>All</Link>
        {categories.map((row) => (
          <Link
            key={row.id}
            className={category === row.id ? "is-on" : ""}
            to={href(row.id)}
            style={{ "--dp-node-hue": categoryHue(row.id) }}
          >
            <span className="dp-nav-text__dot" />
            {row.label}
          </Link>
        ))}
      </nav>

      <div className="dp-index">
        {visibleRest.map((item, index) => (
          <Link key={item.id} to={item.path} style={{ "--dp-node-hue": categoryHue(item.category) }}>
            <span className="dp-index__n">{String(index + 1).padStart(2, "0")}</span>
            <span className="dp-index__cat">
              <span className="dp-index__dot" />
              {guideCategoryLabel(item.category, categories)}
            </span>
            <h3>{item.title}</h3>
          </Link>
        ))}
      </div>

      <section className="dp-scene dp-scene--tight">
        <SceneIndex n={2} />
        <p className="dp-kicker">NEXT</p>
        <h2 className="dp-display" style={{ fontSize: "clamp(2rem, 5vw, 3.4rem)" }}>프로젝트 범위를<br />정리합니다.</h2>
        <PreviewCta onClick={openContactForm}>상담 신청</PreviewCta>
      </section>
    </div>
  );
}
